import React from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'

// Hooks
import { useToast } from '@sketch/toasts'
import { ModalInjectedProps, useModalContext } from 'containers/ModalManager'

// Components
import Modal from 'components/Modal'
import { Button, Form } from '@sketch/components'
import { PasswordInput } from 'components/PasswordInput'
import Confirm2FADisabledModal from 'modules/user/modals/Confirm2FADisabledModal'

// Styles
import { Title, Description } from './Disable2FAModal.styles'

// GQL
import { useDisableMfaMutation } from '@sketch/gql-types'

type FormValues = typeof INITIAL_VALUES

// Helpers
const INITIAL_VALUES = {
  password: '',
}

const VALIDATION_SCHEMA = yup.object().shape({
  password: yup.string().required('Password can’t be blank'),
})

const Disable2FAModal: React.FC<ModalInjectedProps> = ({ hideModal }) => {
  const { showModal } = useModalContext()
  const { showToast } = useToast()

  const [disableMFA, { loading }] = useDisableMfaMutation({
    onCompleted: () => {
      showModal(Confirm2FADisabledModal)
    },
    onError: ({ message }) => {
      showToast(message, 'negative')
    },
  })

  const handleSubmit = (values: FormValues) => {
    disableMFA({
      variables: {
        password: values.password,
      },
    })
  }

  return (
    <Modal onCancel={hideModal}>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, dirty }) => (
          <Form>
            <Modal.Body>
              <Title>Disable Two-Factor Authentication (2FA)?</Title>
              <Description>
                Enter your password to disable two-factor authentication (2FA).
              </Description>

              <Form.Field
                name="password"
                label="Password"
                errorText={touched.password ? errors.password : undefined}
              >
                <PasswordInput
                  name="password"
                  value={values.password}
                  placeholder="Password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Form.Field>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={hideModal} disabled={loading} size="40">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={!dirty || loading}
                size="40"
              >
                Disable 2FA
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default Disable2FAModal
